// Receptionist · Call Logs — mock data
// Presentation/state-only sample content (no API or persistence).

import type { CallLogsKpi, CallLogsTabId, InboundCall, OutboundPatientCall } from './types';

/* ============================================================
   INBOUND CALLS — 6 sample calls
   ============================================================ */
export const INBOUND_CALLS: InboundCall[] = [
  {
    id: 'ic1',
    name: 'Marcia Hollis',
    phone: '(415) 555-0142',
    isReturning: true,
    location: 'San Francisco, CA',
    timeLabel: 'Today, 10:15 am',
    duration: '3:12',
    intent: 'emergency_pain',
    outcome: 'scheduled',
    sentiment: 'anxious',
    summary: [
      'Patient called with severe toothache on lower left side.',
      'Bypassed standard triage due to pain level (8/10).',
      'Verified Delta Dental PPO active before booking.',
    ],
    extracted: [
      { label: 'Insurance', value: 'Verified active Delta Dental PPO' },
      { label: 'Note', value: 'Patient reported allergy to latex' },
      { label: 'Booked', value: 'Emergency slot 2:00 PM today with Dr. Lin. Booking #88492-A' },
    ],
    transcript: [
      { speaker: 'elva', text: 'Hi, this is Elva at Bayside Dental. How can I help you today?', time: '10:14:02 am' },
      { speaker: 'caller', text: "Hi, I'm having a lot of pain. My face is swollen on the left side and it hurts to talk.", time: '10:14:11 am' },
      { reasoning: 'Detected emergency pain. Patient described severe pain plus visible swelling. Marking as urgent and skipping standard triage flow.' },
      { speaker: 'elva', text: "I'm so sorry to hear that. On a scale of one to ten, how would you rate your pain right now?", time: '10:14:18 am' },
      { speaker: 'caller', text: "It's at least an eight. Started last night and it's been getting worse.", time: '10:14:27 am' },
      { reasoning: "Pain rating 8+ triggers same-day emergency protocol. Looking up today's emergency slots before discussing options." },
      { speaker: 'elva', text: 'Understood. Let me find you an emergency slot today. Are you currently a patient with us?', time: '10:14:35 am' },
      { speaker: 'caller', text: 'Yes, I have my insurance through Delta Dental.', time: '10:14:42 am' },
      { reasoning: "Verifying Delta Dental PPO via real-time eligibility. Pulling up Marcia's file from the practice management system." },
      { speaker: 'elva', text: "Verified. I can see you're on Delta Dental PPO, last visit October. I have a 2 PM slot today with Dr. Lin. Can you make it in?", time: '10:14:55 am' },
      { speaker: 'caller', text: "Yes, I'll be there. Thank you so much.", time: '10:15:02 am' },
      { reasoning: 'Booking the 2 PM slot. Sending text confirmation. Notifying Dr. Lin that an emergency walk-in is on the way.' },
      { speaker: 'elva', text: "Wonderful. You're booked for 2 PM with Dr. Lin. I've sent a text confirmation. See you soon, and feel better.", time: '10:15:10 am' },
    ],
  },
  {
    id: 'ic2',
    name: 'Carlos Reyes',
    phone: '(510) 555-0917',
    isReturning: false,
    location: 'Oakland, CA',
    timeLabel: 'Today, 9:48 am',
    duration: '1:55',
    intent: 'emergency_pain',
    outcome: 'transferred',
    sentiment: 'frustrated',
    summary: [
      'New caller, severe tooth pain since yesterday.',
      'Asked twice for a human after Elva offered next-week slots.',
      'Transferred to front desk per warm-transfer rules.',
    ],
    extracted: [
      { label: 'Note', value: 'First-time caller. No insurance on file.' },
      { label: 'Routed to', value: 'Front desk — Sarah K.' },
    ],
    transcript: [
      { speaker: 'elva', text: 'Hi, this is Elva at Bayside Dental. How can I help you?', time: '9:48:02 am' },
      { speaker: 'caller', text: 'I need to see someone today, my tooth is killing me.', time: '9:48:09 am' },
      { reasoning: 'Detected emergency pain. New caller — no record in the practice management system.' },
      { speaker: 'elva', text: "I'm sorry. Are you currently a patient with us?", time: '9:48:14 am' },
      { speaker: 'caller', text: 'No, this is my first time calling. Can I just come in?', time: '9:48:20 am' },
      { reasoning: 'Same-day emergency for new patients requires staff review. Offering next available, then escalating per policy.' },
      { speaker: 'elva', text: 'I have an opening for new patients on Monday at 10. Would that work?', time: '9:48:28 am' },
      { speaker: 'caller', text: 'Monday? No. I need someone today — can I please talk to a person?', time: '9:48:34 am' },
      { reasoning: 'Patient requested human twice. Per warm-transfer rules, transferring to front desk.' },
      { speaker: 'elva', text: 'Of course. Let me get you to our front desk right now. One moment.', time: '9:48:42 am' },
    ],
  },
  {
    id: 'ic3',
    name: 'Eleanor Whitfield',
    phone: '(925) 555-0048',
    isReturning: true,
    location: 'Walnut Creek, CA',
    timeLabel: 'Today, 9:30 am',
    duration: '2:30',
    intent: 'reschedule',
    outcome: 'rescheduled',
    sentiment: 'positive',
    summary: [
      "Patient asked to move next Monday's cleaning.",
      'Found and confirmed Wednesday 2:00 PM with same hygienist.',
      'SMS confirmation sent.',
    ],
    extracted: [
      { label: 'Rescheduled', value: 'Monday 9:00 AM → Wednesday 2:00 PM with Sarah' },
      { label: 'Note', value: 'Patient travels for work, prefers afternoons' },
    ],
    transcript: [
      { speaker: 'elva', text: 'Hi, this is Elva. How can I help?', time: '9:30:02 am' },
      { speaker: 'caller', text: 'Hi, I have a cleaning Monday morning, I need to move it.', time: '9:30:08 am' },
      { reasoning: "Pulling up Eleanor's file. Found Monday 9:00 AM cleaning with Sarah." },
      { speaker: 'elva', text: 'Of course, Eleanor. I see your cleaning Monday at 9 with Sarah. When works better?', time: '9:30:18 am' },
      { speaker: 'caller', text: 'I prefer afternoons if you have anything that week.', time: '9:30:25 am' },
      { reasoning: 'Looking for afternoon slots with Sarah, same week. Found Wednesday 2:00 PM open.' },
      { speaker: 'elva', text: 'Sarah has Wednesday at 2 open. Same week, same hygienist. Want me to move you there?', time: '9:30:36 am' },
      { speaker: 'caller', text: "Yes, that's perfect. Thanks!", time: '9:30:42 am' },
      { reasoning: 'Moving the appointment. Sending text confirmation.' },
      { speaker: 'elva', text: "Done. You'll get a confirmation text in a moment. Anything else?", time: '9:30:55 am' },
    ],
  },
  {
    id: 'ic4',
    name: 'Theo Park',
    phone: '(415) 555-2218',
    isReturning: true,
    location: 'San Francisco, CA',
    timeLabel: 'Today, 8:42 am',
    duration: '4:08',
    intent: 'billing',
    outcome: 'needs_callback',
    sentiment: 'frustrated',
    summary: [
      'Patient disputes a $480 charge for a recent crown.',
      'Has the original estimate document showing $290.',
      'Elva captured the details and scheduled a callback with the office manager.',
    ],
    extracted: [
      { label: 'Disputed amount', value: '$480 charged vs $290 estimated — $190 difference' },
      { label: 'Document', value: 'Patient has original estimate on hand' },
      { label: 'Callback', value: 'Sarah K. — today between 1–3 PM' },
    ],
    transcript: [
      { speaker: 'elva', text: 'Hi, this is Elva at Bayside Dental. How can I help you?', time: '8:42:02 am' },
      { speaker: 'caller', text: "I got a bill for $480 for a crown but my estimate was $290. What's going on?", time: '8:42:11 am' },
      { reasoning: "Billing dispute detected. Pulling up Theo's recent visits and ledger." },
      { speaker: 'elva', text: "I'm sorry about the confusion. I can see the crown was placed November 12th. Let me grab the office manager so we can review the estimate together.", time: '8:42:24 am' },
      { speaker: 'caller', text: 'I have the estimate in front of me. It says $290.', time: '8:42:32 am' },
      { reasoning: 'Patient has documentation. This is a manager-handled escalation per billing-dispute rules. Sarah is in a meeting until 11 AM — offering callback window.' },
      { speaker: 'elva', text: 'Got it. Our manager Sarah is in a meeting this morning. Can she call you back this afternoon between 1 and 3?', time: '8:42:46 am' },
      { speaker: 'caller', text: 'Yes, that works. Make sure she has the estimate.', time: '8:42:54 am' },
      { reasoning: 'Logging the dispute details and the estimate reference. Creating callback task for Sarah with full context.' },
      { speaker: 'elva', text: "I'll have everything queued up for her. She'll have your estimate and the November ledger when she calls.", time: '8:43:08 am' },
    ],
  },
  {
    id: 'ic5',
    name: 'Aisha Patel',
    phone: '(650) 555-3304',
    isReturning: false,
    location: 'Redwood City, CA',
    timeLabel: 'Today, 8:14 am',
    duration: '5:42',
    intent: 'new_patient_exam',
    outcome: 'registered',
    sentiment: 'positive',
    summary: [
      'First-time caller, looking for a regular family dentist.',
      'Has Aetna PPO and 2 kids (ages 7 and 10).',
      'Booked her cleaning and started the new-patient intake.',
    ],
    extracted: [
      { label: 'Insurance', value: 'Aetna PPO — verified active' },
      { label: 'Booked', value: 'Cleaning Tue 11/26 at 11:00 AM with Sarah' },
      { label: 'Forms sent', value: 'New-patient intake · Medical history' },
      { label: 'Note', value: 'Mentioned 2 children, may book pedo consultations later' },
    ],
    transcript: [
      { speaker: 'elva', text: 'Hi, this is Elva at Bayside Dental. How can I help you?', time: '8:14:02 am' },
      { speaker: 'caller', text: "Hi, I'm new to the area and looking for a dentist. Are you taking new patients?", time: '8:14:09 am' },
      { reasoning: "New patient inquiry. Confirming we're accepting and starting registration flow." },
      { speaker: 'elva', text: "Yes, we'd love to have you. What kind of appointment are you looking for — a cleaning, an exam, something specific?", time: '8:14:18 am' },
      { speaker: 'caller', text: 'Just a cleaning to start. And I have Aetna.', time: '8:14:25 am' },
      { reasoning: 'Verifying Aetna PPO via real-time eligibility check.' },
      { speaker: 'elva', text: "Aetna PPO is verified — you're all set there. Let me find a cleaning slot. Are mornings or afternoons better?", time: '8:14:38 am' },
      { speaker: 'caller', text: 'Mornings if you have something next week.', time: '8:14:44 am' },
      { reasoning: "Looking at next week's morning hygiene slots. Tuesday 11 AM with Sarah is open." },
      { speaker: 'elva', text: 'Sarah has Tuesday the 26th at 11 AM open. Does that work?', time: '8:14:55 am' },
      { speaker: 'caller', text: "Yes, perfect. I have two kids too — maybe I'll book them later.", time: '8:15:04 am' },
      { reasoning: 'Noting interest in pediatric consultations. Not booking now — will surface in follow-up campaign.' },
      { speaker: 'elva', text: "Great. I'll send you the new-patient intake forms by text and email. Once those are done you're all set for Tuesday.", time: '8:15:18 am' },
    ],
  },
  {
    id: 'ic6',
    name: 'Unknown caller',
    phone: '(415) 555-9982',
    isReturning: false,
    location: 'Unknown',
    timeLabel: 'Today, 7:58 am',
    duration: '0:34',
    intent: 'hours_location',
    outcome: 'answered',
    sentiment: 'neutral',
    summary: [
      'Caller asked for office hours.',
      'Elva confirmed Monday–Friday 8 AM–6 PM, Saturday 9 AM–1 PM.',
      'Caller hung up after the answer.',
    ],
    extracted: [{ label: 'Question', value: 'Office hours' }],
    transcript: [
      { speaker: 'elva', text: 'Hi, this is Elva at Bayside Dental. How can I help?', time: '7:58:02 am' },
      { speaker: 'caller', text: 'What are your hours?', time: '7:58:09 am' },
      { reasoning: 'Quick FAQ. Pulling office hours from the Brain.' },
      { speaker: 'elva', text: "We're open Monday through Friday 8 AM to 6 PM, and Saturdays 9 AM to 1 PM. Anything else I can help with?", time: '7:58:14 am' },
      { speaker: 'caller', text: 'No, thanks.', time: '7:58:22 am' },
    ],
  },
];

/* ============================================================
   OUTBOUND — PATIENT CALLS (Recall-triggered)
   ============================================================ */
export const OUTBOUND_PATIENT_CALLS: OutboundPatientCall[] = [
  {
    id: 'op1',
    name: 'Lisa Park',
    phone: '(415) 555-0188',
    timeLabel: 'Today, 10:30 am',
    duration: '1:42',
    recallContext: {
      sequenceInstanceId: 4421,
      stepInstanceId: 88512,
      recallTypeSlug: 'appointment_confirmation',
      recallName: '24h appointment confirmation',
      segmentName: 'Hygiene appointments',
      goalType: 'retention',
      priorStepSummary: [
        { type: 'email', result: 'opened', sentAt: 'Mon, 9:00 am' },
        { type: 'sms', result: 'no_response', sentAt: 'Tue, 4:00 pm' },
      ],
    },
    attemptNumber: 1,
    maxAttempts: 3,
    status: 'completed',
    primaryOutcome: 'appointment_confirmed',
    additionalActions: [],
    summary: [
      'Lisa confirmed her cleaning tomorrow at 9:30 AM.',
      'Asked about parking validation — Elva confirmed parking is free.',
      'Patient was friendly, took less than 2 minutes.',
    ],
    extracted: [
      { label: 'Confirmed', value: 'Cleaning Wed 11/27 at 9:30 AM with Sarah' },
      { label: 'Question', value: 'Asked about parking validation — confirmed free' },
    ],
    attemptLog: [
      { attempt: 1, time: 'Today, 10:30 am', result: 'Answered — confirmed', outcome: 'completed' },
    ],
    transcript: [
      { speaker: 'elva', text: 'Hi, this is Elva calling from Bayside Dental. Is this Lisa?', time: '10:30:02 am' },
      { speaker: 'caller', text: 'Yes, hi.', time: '10:30:06 am' },
      { reasoning: 'Confirmed identity. Reading the confirmation script from the recall context.' },
      { speaker: 'elva', text: 'Hi Lisa. Just calling to confirm your cleaning tomorrow at 9:30 AM with Sarah. Are you still able to make it?', time: '10:30:12 am' },
      { speaker: 'caller', text: "Yes, I'll be there. Is there parking on the street?", time: '10:30:20 am' },
      { reasoning: 'Quick parking question. We have free street parking + 2-hour validation in the building garage.' },
      { speaker: 'elva', text: 'Yes, the building has 2-hour free parking in the garage. Free street parking too.', time: '10:30:28 am' },
      { speaker: 'caller', text: 'Great, see you tomorrow.', time: '10:30:34 am' },
      { reasoning: 'Logging the confirmation. Updating patient record.' },
      { speaker: 'elva', text: 'See you tomorrow, Lisa.', time: '10:30:38 am' },
    ],
  },
  {
    id: 'op2',
    name: 'David Chen',
    phone: '(415) 555-7733',
    timeLabel: 'Today, 10:08 am',
    duration: '0:32',
    recallContext: {
      sequenceInstanceId: 4422,
      stepInstanceId: 88516,
      recallTypeSlug: 'hygiene_recall',
      recallName: 'Hygiene recall',
      segmentName: 'Overdue 6-month cleaning',
      goalType: 'retention',
      priorStepSummary: [
        { type: 'email', result: 'no_open', sentAt: 'Last week' },
        { type: 'sms', result: 'no_response', sentAt: 'Yesterday' },
      ],
    },
    attemptNumber: 2,
    maxAttempts: 3,
    status: 'voicemail',
    primaryOutcome: 'voicemail_left',
    additionalActions: [],
    summary: [
      'Second attempt to reach David for an overdue recall.',
      'Reached voicemail — Elva left a friendly reminder.',
      'Will retry tomorrow morning.',
    ],
    voicemailMessage:
      "Hi David, this is Elva from Bayside Dental. Just calling to remind you that you're due for your 6-month cleaning. Give us a call back at (415) 555-0100 when you have a moment, or you can book online. Thanks, and have a great day.",
    attemptLog: [
      { attempt: 1, time: 'Yesterday, 4:30 pm', result: 'No answer', outcome: 'no_answer' },
      { attempt: 2, time: 'Today, 10:08 am', result: 'Voicemail left', outcome: 'voicemail' },
    ],
    transcript: null,
  },
  {
    id: 'op3',
    name: 'Patricia Lee',
    phone: '(415) 555-2240',
    timeLabel: 'Today, 9:52 am',
    duration: '2:14',
    recallContext: {
      sequenceInstanceId: 4423,
      stepInstanceId: 88521,
      recallTypeSlug: 'treatment_plan_followup',
      recallName: 'Treatment plan follow-up',
      segmentName: 'Crown discussed last visit',
      goalType: 'revenue',
      priorStepSummary: [{ type: 'email', result: 'opened', sentAt: '5 days ago' }],
    },
    attemptNumber: 1,
    maxAttempts: 2,
    status: 'completed',
    primaryOutcome: 'appointment_booked',
    additionalActions: [
      { type: 'follow_up_appointment_booked', details: 'Crown prep Thu 12/5 at 2:30 PM with Dr. Lin', appointmentId: 99421 },
    ],
    summary: [
      'Patient ready to schedule the crown discussed last visit.',
      'Booked the prep appointment for Thursday afternoon.',
      'Treatment plan financing question — flagged for review.',
    ],
    extracted: [
      { label: 'Booked', value: 'Crown prep Thu 12/5 at 2:30 PM with Dr. Lin' },
      { label: 'Question raised', value: 'Asked about financing options for the seat appointment — not in Brain' },
    ],
    attemptLog: [{ attempt: 1, time: 'Today, 9:52 am', result: 'Answered — booked', outcome: 'completed' }],
    transcript: [
      { speaker: 'elva', text: 'Hi, this is Elva from Bayside Dental. Is this Patricia?', time: '9:52:02 am' },
      { speaker: 'caller', text: 'Yes.', time: '9:52:05 am' },
      { reasoning: 'Following up on the crown treatment plan discussed at last visit.' },
      { speaker: 'elva', text: "Hi Patricia. I'm following up on the crown Dr. Lin recommended. Did you want to go ahead and schedule the prep?", time: '9:52:12 am' },
      { speaker: 'caller', text: "Yes, I've been meaning to call. What do you have?", time: '9:52:20 am' },
      { reasoning: 'Looking for crown prep slots with Dr. Lin. Thursday 2:30 PM is open.' },
      { speaker: 'elva', text: 'Dr. Lin has Thursday December 5th at 2:30 open. Does that work?', time: '9:52:32 am' },
      { speaker: 'caller', text: 'Yes. Quick question — do you have payment plans for the seat appointment?', time: '9:52:40 am' },
      { reasoning: 'Financing question — not in current Brain. Flagging for office manager review. Will not improvise an answer.' },
      { speaker: 'elva', text: "Let me have our office manager Sarah follow up on financing options — she can walk you through what we offer. I'll get you booked for the prep now.", time: '9:52:55 am' },
    ],
  },
  {
    id: 'op4',
    name: 'Marcus Brown',
    phone: '(415) 555-4471',
    timeLabel: 'Today, 9:14 am',
    duration: '0:48',
    recallContext: {
      sequenceInstanceId: 4424,
      stepInstanceId: 88528,
      recallTypeSlug: 'hygiene_recall',
      recallName: 'Hygiene recall',
      segmentName: '9 months since last cleaning',
      goalType: 'retention',
      priorStepSummary: [
        { type: 'email', result: 'no_open', sentAt: 'Mon morning' },
        { type: 'sms', result: 'no_response', sentAt: 'Tue morning' },
      ],
    },
    attemptNumber: 3,
    maxAttempts: 3,
    status: 'no_answer',
    primaryOutcome: 'no_contact',
    additionalActions: [],
    summary: [
      'Third and final attempt for the recall campaign.',
      'No answer, no voicemail box configured.',
      'Will mark patient as recall-exhausted and surface to staff.',
    ],
    attemptLog: [
      { attempt: 1, time: 'Mon, 4:00 pm', result: 'No answer', outcome: 'no_answer' },
      { attempt: 2, time: 'Wed, 10:30 am', result: 'Voicemail left', outcome: 'voicemail' },
      { attempt: 3, time: 'Today, 9:14 am', result: 'No answer', outcome: 'no_answer' },
    ],
    transcript: null,
  },
];

/* ============================================================
   KPI CONFIGS — per-tab values shown in the mini-strip
   ============================================================ */
export const KPIS_BY_TAB: Record<CallLogsTabId, CallLogsKpi[]> = {
  inbound: [
    { label: 'Calls today', value: 24, sub: 'across all intents', icon: 'phone-in', sentiment: 'neutral', delta: 5 },
    { label: 'Completed by Elva', value: 18, sub: '75% resolved solo', icon: 'check-circle', sentiment: 'positive', delta: 4 },
    { label: 'Transferred to staff', value: 4, sub: 'warm handoffs', icon: 'alert', sentiment: 'warning', delta: -1 },
    { label: 'Avg duration', value: '2m 38s', sub: 'median 2m 14s', icon: 'clock', sentiment: 'neutral', delta: null },
  ],
  outboundPatient: [
    { label: 'Outbound today', value: 19, sub: 'confirmations + recalls', icon: 'phone-out', sentiment: 'neutral', delta: 3 },
    { label: 'Confirmations completed', value: 11, sub: 'appointments confirmed', icon: 'check-circle', sentiment: 'positive', delta: 2 },
    { label: 'Reschedules booked', value: 3, sub: 'rebooked without staff', icon: 'check-circle', sentiment: 'positive', delta: 1 },
    { label: 'Voicemails left', value: 5, sub: 'will retry tomorrow', icon: 'voicemail', sentiment: 'neutral', delta: null },
  ],
};
